/**
 * BenchSection Component
 * 
 * Displays the substitutes bench with slots for players.
 */
import { Card, CardContent } from '../ui/card';
import { X } from 'lucide-react';

/**
 * @param {Object} props
 * @param {Object[]} props.subs - Array of sub objects with playerId
 * @param {Object[]} props.players - All players (for looking up sub info)
 * @param {Function} props.onBenchPositionClick - Handler for clicking a bench position
 * @param {Function} props.onBenchDoubleClick - Handler for double-clicking a bench position (change number)
 * @param {Function} props.onRemoveSub - Handler to remove substitute
 * @param {boolean} props.isViewOnly - Whether the selector is read-only
 * @param {number|null} props.selectedBenchPosition - Currently selected bench position
 * @param {Object} props.subNumbers - Map of bench index to assigned shirt number
 * @param {number} props.benchSlots - Number of bench slots available
 */
export function BenchSection({
  subs = [],
  players = [],
  onBenchPositionClick,
  onBenchDoubleClick,
  onRemoveSub,
  isViewOnly = false,
  selectedBenchPosition = null,
  subNumbers = {},
  benchSlots = 7
}) {
  // Get player by ID
  const getPlayerById = (playerId) => players.find(p => p.id === playerId);
  
  // Create array of bench slots (filled or empty)
  const benchSlotsArray = Array.from({ length: benchSlots }, (_, index) => {
    const sub = subs[index];
    if (sub && sub.playerId) {
      return {
        index,
        playerId: sub.playerId,
        player: getPlayerById(sub.playerId),
        shirtNumber: subNumbers[index] || subNumbers[String(index)] || sub.number
      };
    }
    return { index, playerId: null, player: null, shirtNumber: null };
  });

  return (
    <Card className="rounded-2xl mt-4">
      <CardContent className="p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">
          Substitutes ({subs.filter(s => s && s.playerId).length}/{benchSlots})
        </h4>
        <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
          {benchSlotsArray.map((slot) => {
            const isSelected = selectedBenchPosition === slot.index;
            const hasPlayer = slot.playerId && slot.player;
            
            return (
              <div
                key={slot.index}
                className={`relative p-2 rounded-lg cursor-pointer transition-all ${
                  hasPlayer 
                    ? 'bg-emerald-50 hover:bg-emerald-100' 
                    : isSelected 
                    ? 'bg-amber-100 ring-2 ring-amber-400' 
                    : 'bg-gray-100 hover:bg-gray-200 border-2 border-dashed border-gray-300'
                }`}
                onClick={() => !isViewOnly && onBenchPositionClick?.(slot.index)}
                onDoubleClick={() => !isViewOnly && hasPlayer && onBenchDoubleClick?.(slot.index)}
                data-testid={`bench-slot-${slot.index}`}
              >
                {hasPlayer ? (
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center text-sm font-bold text-white mb-1">
                      {slot.shirtNumber || '?'}
                    </div>
                    <span className="text-xs text-gray-700 text-center truncate w-full">
                      {slot.player?.name?.split(' ').pop() || 'Unknown'}
                    </span>
                    {!isViewOnly && (
                      <button
                        className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveSub?.(slot.index);
                        }}
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-1">
                    <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-sm font-bold text-gray-500 mb-1">
                      {slot.index + 1}
                    </div>
                    <span className="text-xs text-gray-400">
                      {isSelected ? 'Select' : 'Empty'}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default BenchSection;
